/**
 * Login Page - CXO Command Centre
 * Secure authentication with MFA (SMS OTP) support
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowLeft,
  Shield,
  Building2,
  AlertCircle,
  Loader,
  Smartphone
} from 'lucide-react';

const Login = () => {
  const { login, verifyMFA, error, clearError } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otpCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError('');
    clearError();
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setFormError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setFormError('Please enter a valid email');
      return false;
    }
    if (!formData.password) {
      setFormError('Password is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setFormError('');
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.requires_mfa) {
        setMfaStep(true);
        setSessionId(result.session_id);
        setLoading(false);
        return;
      }
      
      // Login successful - will redirect via App.js
    } catch (err) {
      setFormError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.otpCode || formData.otpCode.length !== 6) {
      setFormError('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    setFormError('');
    
    try {
      await verifyMFA(sessionId, formData.otpCode);
      // Login successful - will redirect via App.js
    } catch (err) {
      setFormError(err.message || 'Verification failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Left Side - Branding */}
      <div style={styles.leftPanel}>
        <div style={styles.brandContent}>
          <Building2 size={56} style={{ opacity: 0.9 }} />
          <h1 style={styles.brandTitle}>CXO COMMAND CENTRE</h1>
          <p style={styles.brandTagline}>
            Workforce analytics, payroll intelligence, and AI-powered decision support in one secure center
          </p>
          
          <div style={styles.securityBadges}>
            <div style={styles.badge}>
              <Shield size={16} />
              <span>Enterprise Security</span>
            </div>
            <div style={styles.badge}>
              <Lock size={16} />
              <span>MFA Protected</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={styles.rightPanel}>
        <div style={styles.loginCard}>
          <button 
            style={styles.backButton}
            onClick={() => mfaStep ? setMfaStep(false) : navigate('/')}
          >
            <ArrowLeft size={18} />
            {mfaStep ? 'Back to Login' : 'Back to Home'}
          </button>

          <div style={styles.loginHeader}>
            <h2 style={styles.loginTitle}>
              {mfaStep ? 'Verify Your Identity' : 'Welcome Back'}
            </h2>
            <p style={styles.loginSubtitle}>
              {mfaStep 
                ? 'Enter the 6-digit code sent to your registered mobile number'
                : 'Sign in to access your CXO Command Centre'}
            </p>
          </div>

          {/* Error Alert */}
          {(formError || error) && (
            <div style={styles.alert}>
              <AlertCircle size={18} />
              <span>{formError || error}</span>
            </div>
          )}

          {/* Login Form or MFA Form */}
          {!mfaStep ? (
            <form onSubmit={handleSubmit} style={styles.form}>
              {/* Email Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address</label>
                <div style={styles.inputWrapper}>
                  <Mail size={18} style={styles.inputIcon} />
                  <input
                    type="email"
                    name="email"
                    style={styles.input}
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrapper}>
                  <Lock size={18} style={styles.inputIcon} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    style={styles.input}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    style={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                style={styles.submitButton}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size={20} className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Signing in...</span>
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMFASubmit} style={styles.form}>
              {/* OTP Input */}
              <div style={styles.formGroup}>
                <div style={styles.mfaIconWrapper}>
                  <Smartphone size={48} style={{ color: '#22d3ee' }} />
                </div>
                <label style={{ ...styles.label, textAlign: 'center' }}>
                  SMS Verification Code
                </label>
                <div style={styles.inputWrapper}>
                  <Shield size={18} style={styles.inputIcon} />
                  <input
                    type="text"
                    name="otpCode"
                    style={{ ...styles.input, textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }}
                    placeholder="000000"
                    value={formData.otpCode}
                    onChange={handleChange}
                    disabled={loading}
                    maxLength={6}
                    pattern="\d{6}"
                    autoComplete="one-time-code"
                  />
                </div>
                <p style={styles.helperText}>
                  A 6-digit code has been sent to your registered mobile number
                </p>
              </div>

              <button
                type="submit"
                style={styles.submitButton}
                disabled={loading || formData.otpCode.length !== 6}
              >
                {loading ? (
                  <>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Verifying...</span>
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Powered By */}
        <div style={styles.poweredBy}>
          Powered by <strong style={{ color: '#22d3ee' }}>Spera Digital ®</strong>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    background: 'radial-gradient(circle at 30% 20%, #1e3a5f 0%, #101936 38%, #070b18 100%)',
  },
  brandContent: {
    textAlign: 'center',
    color: 'white',
    maxWidth: '450px',
  },
  brandTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '2.75rem',
    fontWeight: '700',
    marginTop: '1.5rem',
    marginBottom: '1rem',
    background: 'linear-gradient(135deg, #ffffff 0%, #67e8f9 48%, #c4b5fd 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  brandTagline: {
    fontSize: '1.0625rem',
    lineHeight: '1.7',
    opacity: '0.9',
    marginBottom: '2rem',
  },
  securityBadges: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '9999px',
    fontSize: '0.8125rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    background: 'linear-gradient(145deg, #070b18 0%, #0d1328 100%)',
  },
  loginCard: {
    width: '100%',
    maxWidth: '440px',
    background: 'rgba(11, 16, 34, 0.96)',
    borderRadius: '16px',
    padding: '2.5rem',
    boxShadow: '0 25px 70px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(34, 211, 238, 0.18)',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: 0,
    marginBottom: '1.5rem',
    transition: 'color 0.2s ease',
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  loginTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#f8fafc',
    marginBottom: '0.5rem',
  },
  loginSubtitle: {
    color: '#64748b',
    fontSize: '0.9375rem',
  },
  alert: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    fontSize: '0.875rem',
    background: 'rgba(227, 24, 55, 0.1)',
    border: '1px solid rgba(227, 24, 55, 0.3)',
    color: '#e31837',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#cbd5e1',
    marginBottom: '0.5rem',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    color: '#94a3b8',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '0.875rem 1rem 0.875rem 2.75rem',
    fontSize: '0.9375rem',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    background: '#070b18',
    color: '#e2e8f0',
    outline: 'none',
  },
  passwordToggle: {
    position: 'absolute',
    right: '1rem',
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  mfaIconWrapper: {
    textAlign: 'center',
    marginBottom: '1rem',
  },
  helperText: {
    fontSize: '0.8125rem',
    color: '#64748b',
    marginTop: '0.5rem',
    textAlign: 'center',
  },
  submitButton: {
    width: '100%',
    padding: '0.875rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
    boxShadow: '0 8px 24px rgba(34, 211, 238, 0.25)',
    marginTop: '0.5rem',
  },
  poweredBy: {
    marginTop: '2rem',
    fontSize: '0.8125rem',
    color: '#64748b',
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default Login;
